import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, Upload, Wand2, ListChecks, Crown, LogOut, X, Check,
  Loader2, ImageIcon, Trash2, Star, MapPin, Lock, RefreshCw,
  HeartPulse, ShieldCheck, AlertTriangle, ShieldAlert, Camera, PencilRuler, Plus, FileText, CalendarCheck,
  ChevronDown, CreditCard, CalendarX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { AngleGuide } from "@/components/AngleGuide";
import { CameraCapture } from "@/components/CameraCapture";
import api, { API } from "@/lib/api";
import { analyzeImageQuality } from "@/lib/imageQuality";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const money = (n) => `$${n.toLocaleString()}`;

const ANGLES = [
  { key: "front", label: "Front", hint: "Face-on", required: true },
  { key: "left", label: "Left side", hint: "Left profile" },
  { key: "right", label: "Right side", hint: "Right profile" },
  { key: "extra", label: "Other", hint: "¾ or body" },
];

const HEALTH_MAP = {
  pending: { label: "Screening needed", cls: "bg-brand-sand text-brand-slate", icon: HeartPulse },
  eligible: { label: "Cleared", cls: "bg-brand-primary/10 text-brand-primary", icon: ShieldCheck },
  caution: { label: "Proceed with caution", cls: "bg-brand-accent/15 text-brand-accent", icon: AlertTriangle },
  blocked: { label: "Not eligible", cls: "bg-destructive/10 text-destructive", icon: ShieldAlert },
};

const TierBadge = ({ tier }) => {
  const map = {
    free: { label: "Free Trial", cls: "bg-brand-sand text-brand-slate" },
    pro: { label: "Pro", cls: "bg-brand-primary/10 text-brand-primary" },
    premium: { label: "Premium", cls: "bg-brand-accent/15 text-brand-accent" },
  };
  const t = map[tier] || map.free;
  return <span data-testid="tier-badge" className={`px-3 py-1 rounded-full text-xs font-medium ${t.cls}`}>{t.label}</span>;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setUser, logout, checkAuth } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [selected, setSelected] = useState([]);
  const [photos, setPhotos] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [explanations, setExplanations] = useState([]);
  const [markupUrl, setMarkupUrl] = useState(null);
  const [markupId, setMarkupId] = useState(null);
  const [showMarkup, setShowMarkup] = useState(false);
  const [markingUp, setMarkingUp] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [recsOpen, setRecsOpen] = useState(false);
  const [recs, setRecs] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [healthQuestions, setHealthQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [healthResult, setHealthResult] = useState(null);
  const [savingHealth, setSavingHealth] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [loc, setLoc] = useState({ country: "", state: "", area: "" });
  const [savingLoc, setSavingLoc] = useState(false);
  const [locating, setLocating] = useState(false);
  const [cameraFor, setCameraFor] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [requestedConsults, setRequestedConsults] = useState(new Set());
  const [consultingKey, setConsultingKey] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subActing, setSubActing] = useState(false);
  const [ageDob, setAgeDob] = useState("");
  const [verifyingAge, setVerifyingAge] = useState(false);
  const [ageError, setAgeError] = useState("");

  useEffect(() => {
    api.get("/procedures").then((r) => setProcedures(r.data));
    api.get("/surgical-list").then((r) => setSelected(r.data.procedure_ids || []));
    api.get("/health/questions").then((r) => setHealthQuestions(r.data));
    api.get("/studio-state").then((r) => {
      const a = r.data.angles || {};
      const mapped = Object.fromEntries(Object.entries(a).map(([k, v]) => [k, { id: v.id, url: `${BACKEND}${v.url}` }]));
      if (Object.keys(mapped).length) setPhotos(mapped);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setLoc({ country: user.country || "", state: user.state || "", area: user.area || "" });
  }, [user]);

  // Deep-link from a clinic treatment: preselect a procedure
  useEffect(() => {
    const pid = searchParams.get("procedure");
    if (pid) {
      setSelected((prev) => {
        if (prev.includes(pid)) return prev;
        const next = [...prev, pid];
        api.post("/surgical-list", { procedure_ids: next }).catch(() => {});
        return next;
      });
      toast.success("Added to your list — upload a photo and generate a preview");
      searchParams.delete("procedure");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveList = useCallback((ids) => {
    api.post("/surgical-list", { procedure_ids: ids }).catch(() => {});
  }, []);

  const saveStudioState = (photosObj) => {
    const angles = Object.fromEntries(Object.entries(photosObj).map(([k, v]) => [k, v.id]));
    api.post("/studio-state", { angles }).catch(() => {});
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      saveList(next);
      return next;
    });
  };

  const onFile = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    // Angle Coach: warn on poor lighting / blur (non-blocking)
    try {
      const q = await analyzeImageQuality(file);
      if (q.issues.length) {
        toast.warning(`Angle coach: ${q.issues.join("; ")}. You can still use it or retake.`, { duration: 6000 });
      }
    } catch (_) { /* ignore */ }
    setUploadingKey(key);
    setResultUrl(null); setResultId(null); setMarkupUrl(null); setMarkupId(null); setShowMarkup(false); setExplanations([]);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => {
        const next = { ...p, [key]: { id: res.data.photo_id, url: `${BACKEND}${res.data.url}` } };
        saveStudioState(next);
        return next;
      });
      toast.success("Angle saved");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const removeAngle = (key) => {
    setPhotos((p) => { const n = { ...p }; delete n[key]; saveStudioState(n); return n; });
    setResultUrl(null); setResultId(null); setMarkupUrl(null); setMarkupId(null); setShowMarkup(false); setExplanations([]);
  };

  const orderedPhotoIds = () => ANGLES.map((a) => photos[a.key]).filter(Boolean).map((p) => p.id);
  const primaryPhoto = () => { for (const a of ANGLES) { if (photos[a.key]) return photos[a.key]; } return null; };

  const generate = async () => {
    const ids = orderedPhotoIds();
    if (ids.length === 0) { toast.error("Upload at least one photo (front angle recommended)"); return; }
    if (selected.length === 0) { toast.error("Select at least one procedure"); return; }
    setGenerating(true);
    setResultUrl(null); setResultId(null); setMarkupUrl(null); setMarkupId(null); setShowMarkup(false); setExplanations([]);
    try {
      const res = await api.post("/preview", { photo_ids: ids, procedure_ids: selected });
      setResultUrl(`${BACKEND}${res.data.url}`);
      setResultId(res.data.result_id);
      setExplanations(res.data.explanations || []);
      await checkAuth();
      toast.success(`Preview ready · ${res.data.angles_used} angle${res.data.angles_used > 1 ? "s" : ""} used`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data.detail);
        setUpgradeOpen(true);
      } else if (e.response?.status === 422) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Could not generate preview");
      }
    } finally {
      setGenerating(false);
    }
  };

  const generateMarkup = async () => {
    const ids = orderedPhotoIds();
    if (ids.length === 0 || selected.length === 0) { toast.error("Upload a photo and select procedures first"); return; }
    setMarkingUp(true);
    try {
      const res = await api.post("/preview/markup", { photo_ids: ids, procedure_ids: selected });
      setMarkupUrl(`${BACKEND}${res.data.url}`);
      setMarkupId(res.data.markup_id);
      setShowMarkup(true);
      toast.success("Surgical markup ready");
    } catch (e) {
      if (e.response?.status === 402) { toast.error(e.response.data.detail); setUpgradeOpen(true); }
      else { toast.error("Could not create markup"); }
    } finally {
      setMarkingUp(false);
    }
  };

  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await api.post(
        "/report",
        { procedure_ids: selected, before_id: primaryPhoto()?.id, result_id: resultId, markup_id: markupId },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "auralift-procedure-plan.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Could not create report");
    } finally {
      setDownloadingReport(false);
    }
  };

  const upgrade = async (lookup_key) => {
    try {
      const res = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
    }
  };

  const loadRecs = async () => {
    try {
      const res = await api.get("/recommendations");
      setRecs(res.data);
      setRecsOpen(true);
    } catch (e) {
      const s = e.response?.status;
      if (s === 402) { setUpgradeOpen(true); }
      else if (s === 428 && e.response.data.detail?.includes("health")) { setHealthOpen(true); }
      else if (s === 428) { setLocationOpen(true); }
      else if (s === 403) { toast.error(e.response.data.detail); }
      else { toast.error("Could not load recommendations"); }
    }
  };

  const openRecs = async () => {
    if (user.tier !== "premium") { setUpgradeOpen(true); return; }
    if (user.health_status === "pending") { setHealthResult(null); setHealthOpen(true); return; }
    if (user.health_status === "blocked") {
      toast.error("Based on your health screening you're not eligible for referrals. Please consult your physician.");
      return;
    }
    if (!user.country) { setLocationOpen(true); return; }
    loadRecs();
  };

  const submitHealth = async () => {
    if (!answers.age) { toast.error("Please enter your age"); return; }
    setSavingHealth(true);
    try {
      const res = await api.post("/health/screening", { answers });
      setHealthResult(res.data);
      await checkAuth();
      if (res.data.health_status === "blocked") {
        toast.error("You're not currently eligible for referrals.");
      } else {
        toast.success("Health screening complete");
      }
    } catch (e) {
      toast.error("Could not save screening");
    } finally {
      setSavingHealth(false);
    }
  };

  const submitLocation = async () => {
    if (!loc.country.trim()) { toast.error("Please enter your country"); return; }
    setSavingLoc(true);
    try {
      await api.post("/location", loc);
      await checkAuth();
      setLocationOpen(false);
      toast.success("Location saved");
    } catch (e) {
      toast.error("Could not save location");
    } finally {
      setSavingLoc(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't supported on this device"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const d = await r.json();
          setLoc({
            country: d.countryName || "",
            state: d.principalSubdivision || "",
            area: d.city || d.locality || "",
            lat: latitude,
            lng: longitude,
          });
          toast.success("Location detected");
        } catch (e) {
          toast.error("Could not detect your area — please enter it manually");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error("Location permission denied. Please enter your location manually.");
        } else {
          toast.error("Could not get your location — please enter it manually");
        }
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const requestConsult = async (clinicName, procedure) => {
    const key = `${clinicName}__${procedure}`;
    setConsultingKey(key);
    try {
      const res = await api.post("/consults", { clinic_name: clinicName, procedure, location: recs?.location || "" });
      setRequestedConsults((s) => new Set(s).add(key));
      toast.success(res.data.message);
    } catch (e) {
      toast.error("Could not request consult");
    } finally {
      setConsultingKey(null);
    }
  };

  const health = HEALTH_MAP[user?.health_status] || HEALTH_MAP.pending;
  const HealthIcon = health.icon;
  const locationStr = [user?.area, user?.state, user?.country].filter(Boolean).join(", ");

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      localStorage.removeItem("session_token");
      setUser(null);
      toast.success("Your data has been deleted");
      navigate("/");
    } catch (e) {
      toast.error("Could not delete your data");
    } finally {
      setDeleting(false);
    }
  };

  const openSubscription = async () => {
    setSubOpen(true);
    setSubLoading(true);
    try {
      const res = await api.get("/subscription");
      setSubInfo(res.data);
    } catch (e) {
      setSubInfo(null);
      toast.error("Could not load your subscription");
    } finally {
      setSubLoading(false);
    }
  };

  const cancelSubscription = async () => {
    setSubActing(true);
    try {
      const res = await api.post("/subscription/cancel");
      setSubInfo((s) => ({ ...s, cancel_at_period_end: true, current_period_end: res.data.current_period_end }));
      toast.success("Subscription will cancel at the end of your billing period");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setSubActing(false);
    }
  };

  const reactivateSubscription = async () => {
    setSubActing(true);
    try {
      const res = await api.post("/subscription/reactivate");
      setSubInfo((s) => ({ ...s, cancel_at_period_end: false, current_period_end: res.data.current_period_end }));
      toast.success("Subscription resumed — you're all set");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume subscription");
    } finally {
      setSubActing(false);
    }
  };

  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

  const verifyAge = async () => {
    setAgeError("");
    if (!ageDob) { setAgeError("Please enter your date of birth."); return; }
    setVerifyingAge(true);
    try {
      const res = await api.post("/age/verify", { dob: ageDob });
      if (res.data?.is_adult) {
        setUser((u) => ({ ...u, is_adult: true, dob: ageDob }));
        toast.success("Thanks — you're verified.");
      }
    } catch (e) {
      setAgeError(e?.response?.data?.detail || "You must be 18 or older to use AuraLift.");
    } finally {
      setVerifyingAge(false);
    }
  };

  const procMap = Object.fromEntries(procedures.map((p) => [p.id, p]));
  const selectedProcs = selected.map((id) => procMap[id]).filter(Boolean);
  const totalLow = selectedProcs.reduce((s, p) => s + p.price_low, 0);
  const totalHigh = selectedProcs.reduce((s, p) => s + p.price_high, 0);
  const faceProcs = procedures.filter((p) => p.category === "face");
  const bodyProcs = procedures.filter((p) => p.category === "body");

  const ProcCard = ({ p }) => {
    const on = selected.includes(p.id);
    return (
      <button
        data-testid={`procedure-${p.id}`}
        onClick={() => toggle(p.id)}
        className={`text-left p-5 rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
          on ? "bg-brand-primary text-white border-brand-primary shadow-lg" : "bg-white border-brand-border/60 hover:border-brand-primary/40"
        }`}
      >
        <div className="flex items-start justify-between mb-1">
          <span className="font-heading text-lg">{p.name}</span>
          <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${on ? "bg-white/20" : "border border-brand-border"}`}>
            {on && <Check className="w-3 h-3" />}
          </span>
        </div>
        <p className={`text-xs mb-2 ${on ? "text-white/70" : "text-brand-slate"}`}>{p.description}</p>
        <span className={`text-xs font-medium ${on ? "text-brand-accent" : "text-brand-primary"}`}>{money(p.price_low)} – {money(p.price_high)}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
            <span className="font-heading text-xl">AuraLift</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="nav-consults-link" onClick={() => navigate("/consults")} className="hidden sm:flex items-center gap-1 text-sm text-brand-slate hover:text-brand-primary">
              <CalendarCheck className="w-4 h-4" /> My consults
            </button>
            <button data-testid="nav-clinics-link" onClick={() => navigate("/clinics")} className="hidden sm:flex items-center gap-1 text-sm text-brand-slate hover:text-brand-primary">
              <MapPin className="w-4 h-4" /> Clinics
            </button>
            <TierBadge tier={user?.tier} />
            {user?.tier !== "premium" && (
              <Button data-testid="header-upgrade-btn" onClick={() => setUpgradeOpen(true)} size="sm" className="rounded-full bg-brand-accent hover:bg-brand-accentHover text-white gap-1.5">
                <Crown className="w-4 h-4" /> Upgrade
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-trigger" className="flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 hover:bg-brand-sand/60 transition-colors">
                  {user?.picture
                    ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    : <span className="w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center text-sm font-medium">{(user?.name || user?.email || "?").charAt(0).toUpperCase()}</span>}
                  <ChevronDown className="w-4 h-4 text-brand-slate" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60" data-testid="account-menu">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium truncate">{user?.name || "Your account"}</span>
                    {user?.email && <span className="text-xs text-brand-slate truncate">{user.email}</span>}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-consults" onClick={() => navigate("/consults")} className="gap-2 cursor-pointer">
                  <CalendarCheck className="w-4 h-4" /> My consults
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-clinics" onClick={() => navigate("/clinics")} className="gap-2 cursor-pointer">
                  <MapPin className="w-4 h-4" /> Find clinics
                </DropdownMenuItem>
                {user?.tier === "free" ? (
                  <DropdownMenuItem data-testid="menu-upgrade" onClick={() => setUpgradeOpen(true)} className="gap-2 cursor-pointer">
                    <Crown className="w-4 h-4" /> Upgrade plan
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="menu-subscription" onClick={openSubscription} className="gap-2 cursor-pointer">
                    <CreditCard className="w-4 h-4" /> Manage subscription
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-delete" onClick={() => setDeleteOpen(true)} className="gap-2 cursor-pointer text-destructive focus:text-destructive">
                  <Trash2 className="w-4 h-4" /> Delete my data
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="gap-2 cursor-pointer">
                  <LogOut className="w-4 h-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_340px] gap-8">
        {/* Left column */}
        <div className="space-y-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mb-1">Your visualization studio</h1>
            <p className="text-brand-slate">Upload a photo, pick procedures, and preview your new look.</p>
          </div>

          <div data-testid="ai-disclaimer" className="rounded-xl border border-brand-accent/30 bg-brand-accent/5 px-4 py-3 flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-brand-accent shrink-0 mt-0.5" />
            <p className="text-xs text-brand-slate leading-relaxed">
              <span className="font-medium text-brand-ink">AI simulation — not a medical outcome.</span> These previews are artistic AI simulations for illustration only. They do <span className="font-medium">not</span> account for your individual biological healing response, your unique anatomical structure, or the real-world limitations of surgery. Actual results vary and are not guaranteed. <span className="font-medium">The outcomes shown are only relevant to this simulation and do not necessarily reflect the results other patients may experience.</span> All cosmetic procedures carry risks — always consult an AHPRA-registered / board-certified surgeon before making any decision. AuraLift does not supply, prescribe or advertise any medicine.
            </p>
          </div>

          {/* Upload angles / Preview */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-brand-slate">Your photos</p>
                <span className="text-xs text-brand-slate flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> More angles = more accurate</span>
              </div>
              <div className="grid grid-cols-2 gap-3" data-testid="angle-grid">
                {ANGLES.map((a) => {
                  const p = photos[a.key];
                  const busy = uploadingKey === a.key;
                  return (
                    <div key={a.key} className="relative">
                      {p ? (
                        <div className="relative aspect-square rounded-2xl overflow-hidden bg-brand-sand">
                          <img src={p.url} alt={a.label} className="w-full h-full object-cover" />
                          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px]">{a.label}</span>
                          <button data-testid={`remove-angle-${a.key}`} onClick={() => removeAngle(a.key)} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center hover:bg-white">
                            <X className="w-3.5 h-3.5 text-brand-ink" />
                          </button>
                        </div>
                      ) : (
                        <label
                          data-testid={`upload-angle-${a.key}`}
                          className="relative aspect-square rounded-2xl border-2 border-dashed border-brand-border bg-white/60 flex flex-col items-center justify-center cursor-pointer hover:border-brand-primary/50 transition-colors text-center px-2 overflow-hidden"
                        >
                          <AngleGuide angle={a.key} />
                          {busy ? (
                            <Loader2 className="w-6 h-6 text-brand-primary animate-spin relative z-10" />
                          ) : (
                            <div className="relative z-10 flex flex-col items-center">
                              <Plus className="w-5 h-5 text-brand-primary mb-1" strokeWidth={1.5} />
                              <span className="text-sm font-medium">{a.label}{a.required && " *"}</span>
                              <span className="text-[11px] text-brand-slate">{a.hint}</span>
                              <span className="text-[10px] text-brand-slate/70 mt-1">Tap for gallery</span>
                            </div>
                          )}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(a.key, e.target.files[0])} />
                          <button
                            type="button"
                            data-testid={`camera-angle-${a.key}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCameraFor(a.key); }}
                            className="absolute bottom-2 right-2 z-20 w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center shadow-md hover:bg-brand-primaryHover"
                            title="Take photo"
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 h-5">
                <p className="text-sm font-medium text-brand-slate">Result</p>
                {resultUrl && markupUrl && (
                  <div className="flex gap-1 text-xs">
                    <button data-testid="toggle-after" onClick={() => setShowMarkup(false)} className={`px-2.5 py-1 rounded-full ${!showMarkup ? "bg-brand-primary text-white" : "text-brand-slate"}`}>After</button>
                    <button data-testid="toggle-markup" onClick={() => setShowMarkup(true)} className={`px-2.5 py-1 rounded-full ${showMarkup ? "bg-brand-primary text-white" : "text-brand-slate"}`}>Markup</button>
                  </div>
                )}
              </div>
              {resultUrl ? (
                showMarkup && markupUrl ? (
                  <div className="relative aspect-[3/4] max-h-[440px] rounded-2xl overflow-hidden bg-brand-sand" data-testid="markup-image">
                    <img src={markupUrl} alt="surgical markup" className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-brand-accent/90 text-white text-xs">Surgical markup</span>
                  </div>
                ) : (
                  <BeforeAfterSlider before={primaryPhoto()?.url} after={resultUrl} />
                )
              ) : (
                <div className="aspect-[3/4] max-h-[440px] rounded-2xl border border-brand-border/60 bg-white/60 flex flex-col items-center justify-center text-center px-6">
                  {generating ? (
                    <>
                      <Loader2 className="w-8 h-8 text-brand-primary animate-spin mb-3" />
                      <p className="font-medium">Rendering your preview…</p>
                      <p className="text-sm text-brand-slate">This can take up to a minute.</p>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-8 h-8 text-brand-slate/50 mb-3" strokeWidth={1.5} />
                      <p className="text-sm text-brand-slate">Your before / after preview will appear here.</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="generate-btn"
              onClick={generate}
              disabled={generating || markingUp}
              size="lg"
              className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2 px-8 h-12 transition-transform hover:-translate-y-0.5"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Generate preview
            </Button>
            {resultUrl && (
              <Button
                data-testid="markup-btn"
                onClick={generateMarkup}
                disabled={markingUp || generating}
                size="lg"
                variant="outline"
                className="rounded-full border-brand-primary/40 text-brand-primary hover:bg-brand-primary/5 gap-2 px-6 h-12"
              >
                {markingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <PencilRuler className="w-4 h-4" />}
                {user?.tier === "free" && <Lock className="w-3.5 h-3.5" />}
                Surgical markup
              </Button>
            )}
            {resultUrl && (
              <Button
                data-testid="report-btn"
                onClick={downloadReport}
                disabled={downloadingReport}
                size="lg"
                variant="outline"
                className="rounded-full border-brand-accent/50 text-brand-accent hover:bg-brand-accent/5 gap-2 px-6 h-12"
              >
                {downloadingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Download report
              </Button>
            )}
          </div>

          {/* What will change */}
          {explanations.length > 0 && (
            <div className="bg-white rounded-2xl border border-brand-border/60 p-6" data-testid="explanations-panel">
              <h3 className="font-heading text-xl mb-4">What this changes</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {explanations.map((ex) => (
                  <div key={ex.name}>
                    <p className="font-medium text-sm mb-1">{ex.name} <span className="text-brand-slate font-normal">· {ex.area}</span></p>
                    <ul className="space-y-1">
                      {ex.changes.map((c) => (
                        <li key={c} className="flex items-start gap-2 text-sm text-brand-slate">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-brand-primary shrink-0" /> {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="text-xs text-brand-slate mt-4">AI visualization only — actual results vary. Discuss specifics with a licensed surgeon.</p>
            </div>
          )}

          {/* Procedures */}
          <div>
            <h2 className="font-heading text-2xl tracking-tight mb-4">Choose procedures</h2>
            <Tabs defaultValue="face">
              <TabsList className="rounded-full bg-brand-sand p-1 mb-5">
                <TabsTrigger value="face" data-testid="tab-face" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-brand-primary px-6">Face</TabsTrigger>
                <TabsTrigger value="body" data-testid="tab-body" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-brand-primary px-6">Body</TabsTrigger>
              </TabsList>
              <TabsContent value="face">
                <div className="grid sm:grid-cols-2 gap-3">
                  {faceProcs.map((p) => <ProcCard key={p.id} p={p} />)}
                </div>
              </TabsContent>
              <TabsContent value="body">
                <div className="grid sm:grid-cols-2 gap-3">
                  {bodyProcs.map((p) => <ProcCard key={p.id} p={p} />)}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Surgical list sidebar */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-white rounded-3xl border border-brand-border/60 p-6 shadow-[0_8px_30px_rgba(74,93,82,0.08)]">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
              <h3 className="font-heading text-xl">Surgical list</h3>
            </div>
            {selectedProcs.length === 0 ? (
              <p className="text-sm text-brand-slate py-6 text-center">No procedures selected yet. Tap a procedure to add it.</p>
            ) : (
              <div className="space-y-3 mb-5" data-testid="surgical-list">
                <AnimatePresence>
                  {selectedProcs.map((p) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center justify-between gap-2 pb-3 border-b border-brand-border/50 last:border-0"
                    >
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-brand-slate">{money(p.price_low)} – {money(p.price_high)}</p>
                      </div>
                      <button data-testid={`remove-${p.id}`} onClick={() => toggle(p.id)} className="text-brand-slate hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {selectedProcs.length > 0 && (
              <div className="rounded-2xl bg-brand-sand/70 p-4 mb-4">
                <p className="text-xs text-brand-slate mb-1">Estimated total</p>
                <p className="font-heading text-2xl" data-testid="total-estimate">{money(totalLow)} – {money(totalHigh)}</p>
              </div>
            )}

            <Button
              data-testid="recommendations-btn"
              onClick={openRecs}
              disabled={selectedProcs.length === 0}
              className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2"
            >
              {user?.tier !== "premium" && <Lock className="w-4 h-4" />}
              Clinic recommendations
            </Button>
            {user?.tier !== "premium" && (
              <p className="text-xs text-brand-slate text-center mt-2">Premium feature — includes real cost estimates</p>
            )}
          </div>

          {/* Health & eligibility card */}
          <div className="bg-white rounded-3xl border border-brand-border/60 p-6 shadow-[0_8px_30px_rgba(74,93,82,0.08)] mt-6" data-testid="eligibility-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <HeartPulse className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                <h3 className="font-heading text-xl">Eligibility</h3>
              </div>
              <span data-testid="health-status-badge" className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${health.cls}`}>
                <HealthIcon className="w-3 h-3" /> {health.label}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-brand-border/50">
              <span className="text-sm text-brand-slate flex items-center gap-1.5"><HeartPulse className="w-4 h-4" /> Health screening</span>
              <button data-testid="open-health-btn" onClick={() => { setHealthResult(null); setHealthOpen(true); }} className="text-sm text-brand-primary hover:underline">
                {user?.health_status === "pending" ? "Start" : "Update"}
              </button>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-brand-slate flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {locationStr || "Location not set"}</span>
              <button data-testid="open-location-btn" onClick={() => setLocationOpen(true)} className="text-sm text-brand-primary hover:underline">
                {user?.country ? "Change" : "Set"}
              </button>
            </div>

            {user?.health_status === "blocked" && (
              <div className="mt-3 rounded-xl bg-destructive/5 border border-destructive/20 p-3 flex gap-2" data-testid="blocked-note">
                <ShieldAlert className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive/90">Based on your screening, referrals are paused. Please consult your physician before proceeding.</p>
              </div>
            )}
            <p className="text-xs text-brand-slate mt-3">Referrals are available worldwide and matched to your country, state and area — after a quick health check.</p>
            <button
              data-testid="delete-account-btn"
              onClick={() => setDeleteOpen(true)}
              className="mt-4 w-full text-xs text-destructive/80 hover:text-destructive flex items-center justify-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete my data
            </button>
          </div>
        </aside>
      </main>

      {/* Upgrade dialog */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Unlock more with a subscription</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div className="p-6 rounded-2xl border border-brand-border/60">
              <h4 className="font-heading text-xl mb-1">Pro</h4>
              <p className="font-heading text-3xl mb-3">$4.99<span className="text-sm text-brand-slate font-body">/mo</span></p>
              <ul className="space-y-2 text-sm text-brand-slate mb-5">
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand-primary" /> Unlimited previews</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand-primary" /> Combine procedures</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand-primary" /> Build your list</li>
              </ul>
              <Button data-testid="upgrade-pro-btn" onClick={() => upgrade("pro_monthly")} className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white">Choose Pro</Button>
            </div>
            <div className="p-6 rounded-2xl border-2 border-brand-accent bg-brand-accent/5 relative">
              <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-brand-accent text-white text-xs">Best value</span>
              <h4 className="font-heading text-xl mb-1">Premium</h4>
              <p className="font-heading text-3xl mb-3">$8.99<span className="text-sm text-brand-slate font-body">/mo</span></p>
              <ul className="space-y-2 text-sm text-brand-slate mb-5">
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand-accent" /> Everything in Pro</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand-accent" /> Clinic recommendations</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-brand-accent" /> Real cost estimates</li>
              </ul>
              <Button data-testid="upgrade-premium-btn" onClick={() => upgrade("premium_monthly")} className="w-full rounded-full bg-brand-accent hover:bg-brand-accentHover text-white">Go Premium</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recommendations dialog */}
      <Dialog open={recsOpen} onOpenChange={setRecsOpen}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="recommendations-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Clinic recommendations</DialogTitle>
            <DialogDescription className="text-brand-slate">Vetted clinics near you with estimated pricing for your surgical list.</DialogDescription>
          </DialogHeader>
          {recs && (
            <div className="space-y-6 pt-2">
              <div className="rounded-2xl bg-brand-sand/70 p-4">
                <p className="text-xs text-brand-slate mb-1">Total estimated range</p>
                <p className="font-heading text-2xl">{money(recs.total_low)} – {money(recs.total_high)}</p>
              </div>
              {recs.recommendations.map((r) => (
                <div key={r.procedure}>
                  <p className="font-heading text-lg mb-2">{r.procedure} <span className="text-sm text-brand-slate font-body">· {money(r.price_low)} – {money(r.price_high)}</span></p>
                  <div className="space-y-2">
                    {r.clinics.map((c) => {
                      const ckey = `${c.name}__${r.procedure}`;
                      const requested = requestedConsults.has(ckey);
                      return (
                      <div key={c.name} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-brand-border/60">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.name}</p>
                          <p className="text-xs text-brand-slate flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <p className="text-sm font-medium flex items-center gap-1 justify-end"><Star className="w-3 h-3 text-brand-accent fill-brand-accent" /> {c.rating}</p>
                            <p className="text-xs text-brand-slate">{money(c.est)}</p>
                          </div>
                          <Button
                            data-testid={`consult-btn-${c.name.replace(/\s+/g, "-").toLowerCase()}-${r.procedure.replace(/\s+/g, "-").toLowerCase()}`}
                            onClick={() => requestConsult(c.name, r.procedure)}
                            disabled={requested || consultingKey === ckey}
                            size="sm"
                            className={`rounded-full h-8 px-3 text-xs gap-1 ${requested ? "bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/10" : "bg-brand-primary hover:bg-brand-primaryHover text-white"}`}
                          >
                            {consultingKey === ckey ? <Loader2 className="w-3 h-3 animate-spin" /> : requested ? <Check className="w-3 h-3" /> : <CalendarCheck className="w-3 h-3" />}
                            {requested ? "Requested" : "Request consult"}
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Health screening dialog */}
      <Dialog open={healthOpen} onOpenChange={setHealthOpen}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[88vh] overflow-y-auto" data-testid="health-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Health & safety screening</DialogTitle>
            <DialogDescription className="text-brand-slate">
              A quick check so we only refer you to clinics when it's safe. Your answers stay private.
            </DialogDescription>
          </DialogHeader>

          {healthResult ? (
            <div className="pt-2 space-y-4" data-testid="health-result">
              {healthResult.health_status === "blocked" && (
                <div className="rounded-2xl bg-destructive/5 border border-destructive/20 p-4">
                  <div className="flex items-center gap-2 mb-2 text-destructive"><ShieldAlert className="w-5 h-5" /><span className="font-medium">Not currently eligible for referrals</span></div>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-destructive/90">
                    {healthResult.blockers.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                  <p className="text-sm text-brand-slate mt-3">You can still explore visualizations, but please consult a physician before pursuing any procedure.</p>
                </div>
              )}
              {healthResult.health_status !== "blocked" && (
                <div className="rounded-2xl bg-brand-primary/5 border border-brand-primary/20 p-4">
                  <div className="flex items-center gap-2 mb-2 text-brand-primary"><ShieldCheck className="w-5 h-5" /><span className="font-medium">You're cleared to request referrals</span></div>
                  {healthResult.cautions.length > 0 && (
                    <ul className="list-disc pl-5 space-y-1 text-sm text-brand-slate mt-2">
                      {healthResult.cautions.map((c) => <li key={c}>{c}</li>)}
                    </ul>
                  )}
                </div>
              )}
              <Button data-testid="health-done-btn" onClick={() => setHealthOpen(false)} className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white">Done</Button>
            </div>
          ) : (
            <div className="pt-2 space-y-4">
              {healthQuestions.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-4">
                  <Label className="text-sm flex-1">{q.label}</Label>
                  {q.type === "number" ? (
                    <Input
                      data-testid={`health-${q.id}`}
                      type="number"
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                      className="w-20 rounded-xl"
                      placeholder="Age"
                    />
                  ) : (
                    <div className="flex gap-1 shrink-0">
                      <button
                        data-testid={`health-${q.id}-yes`}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: true }))}
                        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${answers[q.id] === true ? "bg-brand-primary text-white border-brand-primary" : "border-brand-border text-brand-slate"}`}
                      >Yes</button>
                      <button
                        data-testid={`health-${q.id}-no`}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: false }))}
                        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${answers[q.id] === false ? "bg-brand-primary text-white border-brand-primary" : "border-brand-border text-brand-slate"}`}
                      >No</button>
                    </div>
                  )}
                </div>
              ))}
              <Button data-testid="health-submit-btn" onClick={submitHealth} disabled={savingHealth} className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2 mt-2">
                {savingHealth && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit screening
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Location dialog */}
      <Dialog open={locationOpen} onOpenChange={setLocationOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Your location</DialogTitle>
            <DialogDescription className="text-brand-slate">
              We match you to clinics worldwide — down to your country, state and area.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2 space-y-4">
            <Button
              data-testid="use-my-location-btn"
              onClick={useMyLocation}
              disabled={locating}
              variant="outline"
              className="w-full rounded-full border-brand-primary/40 text-brand-primary hover:bg-brand-primary/5 gap-2"
            >
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
              Use my current location
            </Button>
            <div className="flex items-center gap-3 text-xs text-brand-slate">
              <span className="flex-1 h-px bg-brand-border" /> or enter manually <span className="flex-1 h-px bg-brand-border" />
            </div>
            <div>
              <Label className="text-sm">Country *</Label>
              <Input data-testid="loc-country" value={loc.country} onChange={(e) => setLoc((l) => ({ ...l, country: e.target.value }))} className="mt-1 rounded-xl" placeholder="e.g. United Kingdom" />
            </div>
            <div>
              <Label className="text-sm">State / Region</Label>
              <Input data-testid="loc-state" value={loc.state} onChange={(e) => setLoc((l) => ({ ...l, state: e.target.value }))} className="mt-1 rounded-xl" placeholder="e.g. Greater London" />
            </div>
            <div>
              <Label className="text-sm">Area / City</Label>
              <Input data-testid="loc-area" value={loc.area} onChange={(e) => setLoc((l) => ({ ...l, area: e.target.value }))} className="mt-1 rounded-xl" placeholder="e.g. Chelsea" />
            </div>
            <Button data-testid="loc-save-btn" onClick={submitLocation} disabled={savingLoc} className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2">
              {savingLoc && <Loader2 className="w-4 h-4 animate-spin" />}
              Save location
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CameraCapture
        open={!!cameraFor}
        onOpenChange={(v) => { if (!v) setCameraFor(null); }}
        angleLabel={ANGLES.find((a) => a.key === cameraFor)?.label || ""}
        onCapture={(file) => { const key = cameraFor; setCameraFor(null); onFile(key, file); }}
      />

      {/* Age gate — hard 18+ block */}
      <Dialog open={!!user && !user.is_adult} onOpenChange={() => {}}>
        <DialogContent className="max-w-md rounded-3xl [&>button]:hidden" data-testid="age-gate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-brand-primary" /> Confirm you're 18 or over
            </DialogTitle>
            <DialogDescription className="text-brand-slate">
              AuraLift is for adults only. Please enter your date of birth to continue. We use this solely to verify your age.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Label className="text-sm">Date of birth</Label>
              <Input
                data-testid="age-dob-input"
                type="date"
                value={ageDob}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setAgeDob(e.target.value)}
                className="mt-1 rounded-xl"
              />
            </div>
            {ageError && <p className="text-sm text-destructive" data-testid="age-error">{ageError}</p>}
            <Button
              data-testid="age-verify-btn"
              onClick={verifyAge}
              disabled={verifyingAge}
              className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white h-11 gap-2"
            >
              {verifyingAge && <Loader2 className="w-4 h-4 animate-spin" />} Confirm &amp; continue
            </Button>
            <p className="text-xs text-brand-slate text-center">By continuing you confirm you are at least 18 years old.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage subscription */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="subscription-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Your subscription</DialogTitle>
            <DialogDescription className="text-brand-slate">
              Manage your {user?.tier === "premium" ? "Premium" : "Pro"} plan.
            </DialogDescription>
          </DialogHeader>
          {subLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl bg-brand-sand/50 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-brand-slate">Current plan</p>
                  <p className="font-heading text-lg capitalize">{subInfo?.tier || user?.tier}</p>
                </div>
                <TierBadge tier={subInfo?.tier || user?.tier} />
              </div>

              {subInfo?.manageable ? (
                <>
                  {subInfo?.cancel_at_period_end ? (
                    <div className="rounded-2xl border border-brand-accent/30 bg-brand-accent/5 p-4" data-testid="sub-cancel-scheduled">
                      <p className="text-sm text-brand-ink flex items-center gap-2">
                        <CalendarX className="w-4 h-4 text-brand-accent" />
                        Cancels on {fmtDate(subInfo.current_period_end)}
                      </p>
                      <p className="text-xs text-brand-slate mt-1">You'll keep full access until then. Changed your mind?</p>
                      <Button
                        data-testid="reactivate-sub-btn"
                        onClick={reactivateSubscription}
                        disabled={subActing}
                        className="mt-3 w-full rounded-full bg-brand-primary hover:bg-brand-primary/90 text-white gap-2"
                      >
                        {subActing && <Loader2 className="w-4 h-4 animate-spin" />} Resume subscription
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-brand-slate flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" /> Renews on {fmtDate(subInfo.current_period_end)}
                      </p>
                      <Button
                        data-testid="cancel-sub-btn"
                        onClick={cancelSubscription}
                        disabled={subActing}
                        variant="outline"
                        className="w-full rounded-full border-destructive/40 text-destructive hover:bg-destructive/5 gap-2"
                      >
                        {subActing && <Loader2 className="w-4 h-4 animate-spin" />} Cancel subscription
                      </Button>
                      <p className="text-xs text-brand-slate text-center">You'll keep access until the end of your billing period.</p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-brand-slate">
                  We couldn't load your billing details right now. If you need to cancel, please try again shortly or contact support.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete account confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Delete your data?</DialogTitle>
            <DialogDescription className="text-brand-slate">
              This permanently removes your account, photos, previews, surgical list, health screening, reviews and consult requests. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-delete-btn"
              onClick={deleteAccount}
              disabled={deleting}
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white gap-2"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />} Delete everything
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
